import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { PostMedia } from '@/components/PostCard';
import { Button } from '@/ui';
import { LoadState } from '@/ui/LoadState';
import { postPath } from '@/core/lib/postUrl';
import { postsWithMedia } from './meta';
import { useCommunityPosts } from './useCommunityPosts';
import type { Community } from '@/core/models/community';

const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 10px; margin-top: 14px;`;
const Tile = styled(Link)`display: block; aspect-ratio: 1 / 1; border-radius: ${({ theme }) => theme.radius.md}; overflow: hidden;`;
const Empty = styled.div`padding: 24px; text-align: center; font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textMuted}; background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)}; border-radius: ${({ theme }) => theme.radius.md};`;
const Footer = styled.div`display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 12px 0 24px; font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textMuted};`;
const Wrap = styled.div`margin-top: 14px;`;

export function MediaTab({ community }: { community: Community }) {
  const feed = useCommunityPosts(community);
  if (feed.loading || feed.error) {
    return (
      <Wrap>
        <LoadState
          loading={feed.loading}
          error={feed.error}
          retry={feed.retry}
          label="this community's media"
          cards={2}
        />
      </Wrap>
    );
  }
  const media = postsWithMedia(feed.posts);
  // Media is picked out of the posts loaded so far, so "none yet" is only final once
  // every page is in.
  const more = feed.hasMore && (
    <Footer>
      {feed.moreFailed && <span role="alert">Couldn&apos;t load more posts.</span>}
      <Button variant="ghost" onClick={feed.loadMore} disabled={feed.loadingMore}>
        {feed.loadingMore ? 'Loading more posts' : feed.moreFailed ? 'Try again' : 'Load more posts'}
      </Button>
    </Footer>
  );
  if (media.length === 0) {
    return (
      <>
        <Empty>
          {feed.hasMore
            ? 'No media in the newest posts. Load more to look further back.'
            : 'No media in this community yet.'}
        </Empty>
        {more}
      </>
    );
  }
  return (
    <>
      <Grid>
        {/* postPath, not a hand-rolled `/post/${p.id}`: these are whole posts, so
            the link carries the share code AND the author's handle, and the post
            route shape lives in exactly one place (1.24.50). */}
        {media.map((p) => (
          <Tile key={p.id} to={postPath(p)} aria-label="Open post">
            <PostMedia post={p} fill />
          </Tile>
        ))}
      </Grid>
      {more}
    </>
  );
}
